/*
 *  ************************************************************************
 *  *  © [2015 - 2025] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { getPath, getTextContrast } from '../../../../../utils';
import { emailValidation } from '../../../login/util';
import { CloseIcon, SuccessCheckIcon } from './icons';
import './styles.m.css';

let giftCountryList = [];
let importedGiftModule = null;

async function getAccessToken(bridgeKeeperIntegrationId, sketchesHost) {
  const accessTokenUrl = getPath({
    sketchesHost,
    relativePath: `/api/auth/v1/access-token/integrations/${bridgeKeeperIntegrationId}`
  });
  const response = await fetch(accessTokenUrl);
  return response.headers.get('x-integration-token');
}

function useRewardsState(sketchesHost) {
  const opts = useSelector((state) =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'story', 'globalAiElements', 'quiz', 'opts'], {})
  );
  const {
    enabled,
    gameTypeKey,
    gameTypeMode,
    claimButton,
    existingRewardMessage,
    giftSentSuccessfullyMessage,
    recipientPhoneLabel,
    sendAsGiftButton,
    sendGiftButton 
  } = opts || {};

  const defaultCountryCode = useSelector((state) =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'login', 'pageSetting', 'defaultCountryCode'], '+93')
  );

  const bridgeKeeperIntegrationId = useSelector((state) =>
    get(
      state,
      ['qt', 'config', 'pagebuilder-config', 'general', 'accesstypeIntegration', 'bridgeKeeperIntegrationId'],
      ''
    )
  );

  const enableDarkMode = useSelector((state) => get(state, ['header', 'isDarkModeEnabled'], false));
  const general = useSelector((state) => get(state, ['qt', 'config', 'pagebuilder-config', 'general'], {}));
  const { darkPrimaryColor = '#4860BC', primaryColor = '#4860BC' } = general;
  const buttonBgColor = enableDarkMode ? darkPrimaryColor : primaryColor;
  const textColor = getTextContrast(buttonBgColor) === 'dark' ? '#000' : '#fff';

  const gameTypeEnabled = enabled && gameTypeKey;
  const gameTypeHost =
    gameTypeMode === 'staging' ? 'https://gametype.staging.quintype.com' : 'https://gametype.quintype.com';

  const [unclaimedRewards, setUnclaimedRewards] = useState([]);
  const [rewardAlreadyClaimed, setRewardAlreadyClaimed] = useState(false);
  const [actionError, setActionError] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);

  useEffect(() => {
    if (!gameTypeEnabled) return;
    async function fetchProgress() {
      try {
        const accessToken = await getAccessToken(bridgeKeeperIntegrationId, sketchesHost);
        const res = await fetch(`${gameTypeHost}/api/v1/user/progress?publisher_id=${gameTypeKey}`, {
          headers: { Authorization: accessToken }
        });
        if (!res.ok) return;
        const data = await res.json();
        const earnedRewards = get(data, ['earned_rewards']) || [];
        const claimed = earnedRewards.filter((r) => r.status === 'REDEEMED' || r.status === 'GIFTED');
        const unclaimed = earnedRewards.filter((r) => r.status === 'EARNED');

        if (claimed.length > 0) return setRewardAlreadyClaimed(true);
        setUnclaimedRewards(unclaimed);
      } catch (err) {
        console.error('progress fetch error', err);
      }
    }
    fetchProgress();
  }, []);

  const reward = unclaimedRewards[0];

  async function callRewardAction(action, giftTo = '') {
    const accessToken = await getAccessToken(bridgeKeeperIntegrationId, sketchesHost);
    return fetch(`${gameTypeHost}/api/v1/rewards/action?publisher_id=${gameTypeKey}`, {
      method: 'POST',
      headers: { Authorization: accessToken },
      body: JSON.stringify({ reward_id: reward.reward_grant_id, action, gift_to: giftTo })
    });
  }

  async function handleRedeem() {
    try {
      const res = await callRewardAction('REDEEM');

      if (res.status === 409) {
        const data = await res.json();
        if (data.status === 'REDEEMED' || data.status === 'GIFTED') return setRewardAlreadyClaimed(true);
        // user has an active subscription, can't self-redeem — nudge to gift
        return setShowGiftModal(true);
      }

      if (!res.ok) return setActionError(true);

      const data = await res.json();
      if (data.status === 'REDEEMED') setRewardAlreadyClaimed(true);
      else setActionError(true);
    } catch (err) {
      console.error('redeem action error', err);
      setActionError(true);
    }
  }

  async function sendGift(recipient) {
    try {
      const res = await callRewardAction('GIFT', recipient);

      if (res.status === 409) {
        const data = await res.json();
        if (data.status === 'REDEEMED' || data.status === 'GIFTED') {
          setRewardAlreadyClaimed(true);
          return { success: true };
        }
        return { error: data.error || data.message || 'This reward has already been used.' };
      }

      if (!res.ok) return { error: 'Something went wrong. Please try again.' };

      const data = await res.json();
      if (data.status === 'GIFTED') return { success: true };
      return { error: 'Something went wrong. Please try again.' };
    } catch (err) {
      console.error('gift action error', err);
      return { error: 'Something went wrong. Please try again.' };
    }
  }

  return {
    gameTypeEnabled,
    rewardAlreadyClaimed,
    unclaimedRewards,
    actionError,
    showGiftModal,
    setShowGiftModal,
    sendGift,
    handleRedeem,
    markRewardClaimed: () => setRewardAlreadyClaimed(true),
    buttonBgColor,
    textColor,
    enableDarkMode,
    claimButton,
    existingRewardMessage,
    giftSentSuccessfullyMessage,
    recipientPhoneLabel,
    sendAsGiftButton,
    sendGiftButton,
    defaultCountryCode
  };
}

function GiftModal({
  onClose,
  onGift,
  onGiftSuccess,
  recipientPhoneLabel,
  sendGiftButton,
  giftSentSuccessfullyMessage,
  defaultCountryCode
}) {
  const [recipient, setRecipient] = useState('');
  const [giftError, setGiftError] = useState(null);
  const [gifted, setGifted] = useState(false);
  const [countryCodeValue, setCountryCodeValue] = useState(defaultCountryCode);
  const [displayDropdown, setDropdown] = useState(false);
  const [isEmailValid, setEmailValidation] = useState(false);
  const enableCountryCodeForPhone = true;

  useEffect(() => {
    if (importedGiftModule === null) {
      importedGiftModule = import(/* webpackChunkName: "country-telephone-data" */ 'country-telephone-data')
        .then(({ allCountries }) => {
          giftCountryList = allCountries;
        })
        .catch(err => {
          console.error('Failed to load package for country telephone code', err);
          importedGiftModule = null;
        });
    }
  }, []);

  async function handleSendGift() {
    const trimmedRecipient = recipient.trim();
    const isPhoneWithCountryCode = trimmedRecipient.startsWith('+');
    const finalRecipient =
      enableCountryCodeForPhone && !isEmailValid && trimmedRecipient && !isPhoneWithCountryCode
        ? `${countryCodeValue}${trimmedRecipient}`
        : trimmedRecipient;
    const result = await onGift(finalRecipient);
    if (result?.error) setGiftError(result.error);
    else setGifted(true);
  }

  const trimmedRecipientForDisplay = recipient.trim();
  const displayCountryCode = enableCountryCodeForPhone && !isEmailValid && trimmedRecipientForDisplay && !trimmedRecipientForDisplay.startsWith('+');

  const getCountryCodeDropdown = () => {
    return (
      <>
        <div
            styleName='gift-cc-overlay'
            role='button'
            tabIndex={0}
            onClick={() => setDropdown(!displayDropdown)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDropdown(!displayDropdown); }}
          >
          {countryCodeValue}
          <span styleName='gift-cc-chevron'>▾</span>
        </div>
        {displayDropdown && giftCountryList.length ? (
          <div styleName='gift-cc-dropdown-container'>
            {giftCountryList.map((country, index) => (
              <div
                styleName='gift-cc-dropdown'
                key={index}
                onClick={() => {
                  setCountryCodeValue(`+${country?.dialCode}`);
                  setDropdown(false);
                }}
              >
                <span styleName='gift-cc-country-name'>{country?.name}</span>
                <span styleName='gift-cc-country-code'>{`+${country?.dialCode}`}</span>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  if (gifted) {
    return (
      <div styleName='gift-modal-overlay'>
        <div styleName='gift-modal' data-test-id='quiz-gift-modal'>
          <div styleName='gift-modal-header'>
            <button styleName='gift-modal-close' onClick={onGiftSuccess}>
              <CloseIcon />
            </button>
          </div>
          <SuccessCheckIcon />
          <p styleName='gift-success-message' data-test-id='quiz-gift-sent-success-message'>{giftSentSuccessfullyMessage || 'Gift sent successfully!.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div styleName='gift-modal-overlay'>
      <div styleName='gift-modal' data-test-id='quiz-gift-modal'>
        <button styleName='gift-modal-close' onClick={onClose}>
          <CloseIcon />
        </button>
        <div styleName='gift-modal-wrapper'>
          <div styleName='gift-modal-header'>
            <p styleName='gift-modal-label'>{recipientPhoneLabel ||  "Enter recipient's email or phone number"}</p>
          </div>
          <div styleName={displayCountryCode ? 'gift-cc-overlay-wrapper' : ''}>
            <input
              styleName={`gift-modal-input${displayCountryCode ? ' gift-cc-input-space' : ''}`}
              data-test-id='quiz-gift-modal-input'
              type='text'
              value={recipient}
              onChange={(e) => {
                setRecipient(e.target.value);
                setEmailValidation(emailValidation(e.target.value));
              }}
            />
            {displayCountryCode && getCountryCodeDropdown()}
          </div>
          {giftError && <p styleName='reward-action-error'>{giftError}</p>}
          <button styleName='gift-reward-btn' data-test-id='quiz-send-gift-button' onClick={handleSendGift} disabled={!recipient.trim()}>
            {sendGiftButton || 'Send Gift'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionButtons({ onGiftClick, onClaim, userHasSubscriptions, claimButton, sendAsGiftButton }) {
  return (
    <div styleName='action-btns'>
      <button styleName='claim-btn' onClick={onGiftClick}>
        {sendAsGiftButton ||  'Send as Gift'}
      </button>
      <button styleName='claim-btn-secondary' data-test-id='quiz-claim-gift-button' onClick={onClaim} disabled={!!userHasSubscriptions}>
        {claimButton || 'Claim'}
      </button>
    </div>
  );
}

export const Rewards = ({ sketchesHost, userHasSubscriptions }) => {
  const {
    gameTypeEnabled,
    rewardAlreadyClaimed,
    unclaimedRewards,
    actionError,
    showGiftModal,
    setShowGiftModal,
    sendGift,
    handleRedeem,
    markRewardClaimed,
    buttonBgColor,
    textColor,
    enableDarkMode,
    claimButton,
    existingRewardMessage,
    giftSentSuccessfullyMessage,
    recipientPhoneLabel,
    sendAsGiftButton,
    sendGiftButton,
    defaultCountryCode
  } = useRewardsState(sketchesHost);

  if (!gameTypeEnabled || rewardAlreadyClaimed || unclaimedRewards.length === 0) return null;

  const mode = enableDarkMode ? 'dark' : 'light';
  const buttonStyles = { '--reward-btn-bg': buttonBgColor, '--reward-btn-color': textColor };

  return (
    <div styleName='rewards-wrapper' style={buttonStyles}>
      <div styleName='reward-card' data-test-id='quiz-reward-card' className={mode}>
        <div styleName='reward-header'>{existingRewardMessage || '🎁 You already have an existing reward to claim/gift'}</div>
        {actionError && <div styleName='reward-error'>Something went wrong. Please try again.</div>}
        <ActionButtons
          onGiftClick={() => setShowGiftModal(true)}
          onClaim={handleRedeem}
          userHasSubscriptions={userHasSubscriptions}
          claimButton={claimButton}
          sendAsGiftButton={sendAsGiftButton}
        />
      </div>
      {showGiftModal && (
        <GiftModal
          onClose={() => setShowGiftModal(false)}
          onGift={sendGift}
          onGiftSuccess={() => {
            setShowGiftModal(false);
            markRewardClaimed();
          }}
          recipientPhoneLabel={recipientPhoneLabel}
          sendGiftButton={sendGiftButton}
          giftSentSuccessfullyMessage={giftSentSuccessfullyMessage}
          defaultCountryCode={defaultCountryCode}
        />
      )}
    </div>
  );
};
